import { supabase } from '../lib/supabase';

export const quizService = {
  /**
   * Get all quizzes for a specific play
   */
  async getQuizzesByPlay(playId) {
    try {
      const { data, error } = await supabase?.from('quizzes')?.select(`
          *,
          quiz_questions (
            id,
            question_type,
            difficulty_level
          )
        `)?.eq('play_id', playId)?.eq('is_published', true)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      throw err;
    }
  },

  /**
   * Get quiz by ID with all questions
   */
  async getQuizById(quizId) {
    try {
      const { data, error } = await supabase?.from('quizzes')?.select(`
          *,
          quiz_questions (
            *
          )
        `)?.eq('id', quizId)?.single();

      if (error) throw error;

      if (data?.quiz_questions) {
        data?.quiz_questions?.sort((a, b) => a?.order_index - b?.order_index);
      }

      return data;
    } catch (err) {
      console.error('Error fetching quiz:', err);
      throw err;
    }
  },

  /**
   * Submit quiz attempt
   */
  async submitQuizAttempt(quizId, answers, timeTaken, timePerQuestion) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: quiz } = await supabase?.from('quizzes')?.select('*, quiz_questions(*)')?.eq('id', quizId)?.single();

      if (!quiz) throw new Error('Quiz not found');

      let correctCount = 0;
      const processedAnswers = {};

      quiz?.quiz_questions?.forEach(question => {
        const userAnswer = answers?.[question?.id];
        let isCorrect = false;

        if (question?.question_type === 'multiple_choice') {
          isCorrect = userAnswer === question?.correct_answer;
        } else if (question?.question_type === 'character_matching') {
          const correctMatches = question?.correct_answer;
          isCorrect = Object.keys(correctMatches)?.every(
            key => userAnswer?.[key] === correctMatches?.[key]
          );
        } else if (question?.question_type === 'pronunciation') {
          isCorrect = userAnswer === true;
        }

        if (isCorrect) correctCount++;
        processedAnswers[question.id] = {
          userAnswer,
          isCorrect,
          questionType: question?.question_type
        };
      });

      const totalQuestions = quiz?.quiz_questions?.length;
      const score = correctCount;
      const passed = (score / totalQuestions) * 100 >= (quiz?.passing_score || 70);

      const { data: attempt, error } = await supabase?.from('quiz_attempts')?.insert({
          user_id: user?.id,
          quiz_id: quizId,
          answers: processedAnswers,
          score,
          total_questions: totalQuestions,
          passed,
          time_taken_seconds: timeTaken,
          time_per_question: timePerQuestion,
          completed_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      return {
        attemptId: attempt?.id,
        score,
        totalQuestions,
        passed,
        percentage: Math.round((score / totalQuestions) * 100)
      };
    } catch (err) {
      console.error('Error submitting quiz attempt:', err);
      throw err;
    }
  },

  /**
   * Get user's quiz attempts history
   */
  async getUserQuizAttempts(userId, quizId) {
    try {
      const { data, error } = await supabase?.from('quiz_attempts')?.select('*')?.eq('user_id', userId)?.eq('quiz_id', quizId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching quiz attempts:', err);
      throw err;
    }
  },

  /**
   * Get user's quiz statistics
   */
  async getUserQuizStats(userId, playId = null) {
    try {
      let query = supabase?.from('quiz_attempts')?.select(`
          *,
          quizzes (
            title,
            play_id,
            plays (
              title
            )
          )
        `)?.eq('user_id', userId);

      if (playId) {
        query = query?.eq('quizzes.play_id', playId);
      }

      const { data, error } = await query?.order('completed_at', { ascending: false });

      if (error) throw error;

      const stats = {
        totalAttempts: data?.length || 0,
        totalPassed: data?.filter(a => a?.passed)?.length || 0,
        averageScore: data?.length > 0
          ? Math.round(data?.reduce((sum, a) => sum + (a?.score / a?.total_questions * 100), 0) / data?.length)
          : 0,
        recentAttempts: data?.slice(0, 5) || []
      };

      return stats;
    } catch (err) {
      console.error('Error fetching quiz stats:', err);
      throw err;
    }
  }
};